import { Settings2 } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Group } from '@unistocks/components/group/group';
import { Card, CardProps } from '@unistocks/components/layout/card';
import { Row } from '@unistocks/components/layout/flex';
import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { HeaderButton } from '@unistocks/components/page/header/header-button/header-button';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { toast } from '@unistocks/components/toast';
import { SavingsGoalParagraph as Paragraph } from '@unistocks/features/savings-goals/components/cards/savings-goal-paragraph';
import { EditSavingsGoalForm } from '@unistocks/features/savings-goals/components/forms/edit-savings-goal-form';
import { SavingsGoalProps } from '@unistocks/features/savings-goals/components/savings-goal-props';
import { invalidateSavingsGoalQueryById } from '@unistocks/features/savings-goals/utils';
import { SavingsGoalCreateOrUpdateDto } from '@unistocks/proxy/axios-client';
import {
  usePortfoliosGetListQuery
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  useSavingsGoalsUpdateMutation
} from '@unistocks/proxy/axios-client/SavingsGoalsQuery';
import { useToggle } from '@unistocks/shared/hooks/use-toggle';
import {
  formatCurrency,
  formatDate,
  formatPercentage,
  t
} from '@unistocks/shared/localization/index.ts';
import { memo } from 'react';
import { Separator } from 'tamagui';

export const SavingsGoalSettings = memo<SavingsGoalProps & CardProps>(
  ({ savingsGoal, goalCalculation, ...props }) => {
    const queryClient = useQueryClient();

    const [open, toggleOpen, setOpen] = useToggle(false);
    const initialValues: SavingsGoalCreateOrUpdateDto = {
      ...savingsGoal,
      currency: savingsGoal.currency.code
    };

    const {
      data: portfolios,
      isLoading,
      isSuccess
    } = usePortfoliosGetListQuery({
      select: data => data.items.filter(portfolio => savingsGoal.portfolios.includes(portfolio.id))
    });

    const { mutateAsync: updateSavingsGoal } = useSavingsGoalsUpdateMutation(savingsGoal.id, {
      onSuccess: () => {
        invalidateSavingsGoalQueryById(queryClient, savingsGoal.id);
        setOpen(false);
        toast.success(t('::SavingsGoalUpdatedSuccessfully', { 0: savingsGoal.name }));
      }
    });

    return (
      <Card {...props}>
        <Card.Header>
          <Card.Heading>{t('::SavingsGoal:MySavingsGoal')}</Card.Heading>
          <HeaderButton Icon={Settings2} color={'$unistocks'} size={'$1.5'} onPress={toggleOpen} />
        </Card.Header>
        <Card.Body>
          <Group gap={'$2'} separator={<Separator />}>
            <Group.Item>
              <Row justifyContent="space-between">
                <Paragraph>{t('::SavingsGoal:Amount')}:</Paragraph>
                <Paragraph fontWeight={'$7'}>
                  {formatCurrency(savingsGoal.goalValue, savingsGoal.currency.code)}
                </Paragraph>
              </Row>
            </Group.Item>
            <Group.Item>
              <Row justifyContent="space-between">
                <Paragraph>{t('::SavingsGoal:StartDate')}:</Paragraph>
                <Paragraph fontWeight={'$7'}>{formatDate(savingsGoal.startDate)}</Paragraph>
              </Row>
            </Group.Item>
            <Group.Item>
              <Row justifyContent="space-between">
                <Paragraph>{t('::SavingsGoal:GoalDate')}:</Paragraph>
                <Paragraph fontWeight={'$7'}>{formatDate(savingsGoal.endDate)}</Paragraph>
              </Row>
            </Group.Item>
            {savingsGoal.contribution ? (
              <Group.Item>
                <Row justifyContent="space-between">
                  <Paragraph>{t('::SavingsGoal:MonthlyContribution')}:</Paragraph>
                  <Paragraph fontWeight={'$7'}>
                    {formatCurrency(savingsGoal.contribution, savingsGoal.currency.code)}
                  </Paragraph>
                </Row>
              </Group.Item>
            ) : null}
            <Group.Item>
              <Row justifyContent="space-between">
                <Paragraph>{t('::SavingsGoal:AnnualReturns')}:</Paragraph>
                <Paragraph fontWeight={'$7'}>
                  {formatPercentage(savingsGoal.annualReturns)}
                </Paragraph>
              </Row>
            </Group.Item>
            <Group.Item>
              <Row mt={'$3'} justifyContent="space-between">
                <Paragraph size={'$6'} fontWeight={'$7'}>
                  {t('::SavingsGoal:ConnectedPortfolios')}
                </Paragraph>
              </Row>
            </Group.Item>
          </Group>
          {isSuccess
            ? portfolios.map(portfolio => (
                <Row key={portfolio.id} justifyContent="space-between">
                  <Paragraph>{portfolio.displayName}</Paragraph>
                </Row>
              ))
            : null}
          {isLoading ? <Spinner /> : null}
        </Card.Body>

        <FullScreenSheet
          open={open}
          onOpenChange={setOpen}
          headerTitle={t('::EditWhat', { 0: savingsGoal.name })}
          headerIcon={<HeaderIcon Icon={Settings2} />}
        >
          <EditSavingsGoalForm
            onSubmit={updateSavingsGoal}
            onCancel={toggleOpen}
            initialValues={initialValues}
          />
        </FullScreenSheet>
      </Card>
    );
  }
);

SavingsGoalSettings.displayName = 'SavingsGoalSettings';
